// ================================================================================================
// Web Module
// --------
// A web module that can contain multiple things
// ================================================================================================
import _ from "lodash";

import { DebugEx } from "../Utils/DebugEx"
import { isStringEmpty, isDefined } from "@Utils/Micro";
import { WebNode } from './WebNode';
import type { WebThing, WebThingPort } from './WebThing';

import { Plegma } from './Api';
import { PlegmaStatics } from './PlegmaStatics';
import { isWebThingKey, WebThingPortType } from './WebNodeTypes';
// ================================================================================================
export class WebModule {
    // --------------------------------------------------------------------------------------------
    public ModuleKey: string;
    public NodeKey: string;
    // --------------------------------------------------------------------------------------------
    private ModuleName: string;
    private SessionBasePorts: boolean;
    public Things: { [key: string]: WebThing };
    // --------------------------------------------------------------------------------------------
    get Name(): string { return this.ModuleName }
    get Hierarchy(): string { return "/" + this.ModuleName }
    get WebNode(): WebNode { return WebNode.GetInstanceKey(this.NodeKey); }
    get DummyNode(): boolean { return this.WebNode?.DummyNode; }
    // --------------------------------------------------------------------------------------------
    constructor(node: WebNode, moduleKey: string, moduleName?: string, sessionBasePorts: boolean = true) {
        this.ModuleKey = moduleKey;
        this.ModuleName = moduleName;
        this.NodeKey = node.NodeKey;
        this.SessionBasePorts = !this.DummyNode && sessionBasePorts;
        this.Things = {};

        // Add the module to node
        node.AddModule(this);
    }
    // --------------------------------------------------------------------------------------------
    // Private Functions
    // --------------------------------------------------------------------------------------------
    private _updateWebNodeThings() {
        // Update the hierarchical naming
        if (!this.DummyNode && !isStringEmpty(this.Name)) {
            _.forOwn(this.Things, (tvalue) => {
                tvalue.PlegmaThing.Hierarchy = this.Hierarchy;
                tvalue.PlegmaThing.Name = tvalue.Name + "(" + this.Name + ")";
            });
        }
        this.WebNode?.UpdateModuleThings(this);
    }
    // --------------------------------------------------------------------------------------------
    // Public API
    // --------------------------------------------------------------------------------------------
    public AddThing(thing: WebThing): void {
        try {
            this.Things[thing.PlegmaThing.ThingKey] = thing;
            this._updateWebNodeThings();
        } catch (ex) {
            DebugEx.TraceException(ex, "[WebModule] Failed to Add thing for module:" + this.ModuleKey, thing);
        }
    }
    // --------------------------------------------------------------------------------------------
    public RemoveThings(thingKeys: string[], sendToServer: boolean): void {
        if (isDefined(thingKeys) && thingKeys.length > 0) {
            // Remove the appropriate things.
            let removedThings = [];
            _.forEach(thingKeys, thingKey => {
                if (_.has(this.Things, thingKey)) {
                    if (!this.DummyNode)
                        removedThings.push(this.Things[thingKey]);
                    delete this.Things[thingKey];
                }
            });

            // Update module's things.
            this._updateWebNodeThings();

            // Send delete request to server
            if (!this.DummyNode && sendToServer && removedThings.length > 0) {
                DebugEx.TraceDebug("Send delete request to cyan", removedThings);

                // Create the things list
                let plegmaThings: Plegma.Thing[] = _.map(removedThings, t => t.PlegmaThing);
                this.WebNode.YodiWS.SetThings(plegmaThings, Plegma.eThingsOperation.Delete, this.Hierarchy);
            }
        }
    }
    // --------------------------------------------------------------------------------------------
    public UpdateThing(thing: WebThing): void {
        try {
            // Update the thing by replace the old one
            if (_.has(this.Things, thing.PlegmaThing.ThingKey)) {
                delete this.Things[thing.PlegmaThing.ThingKey];
            }

            // Find if the thing contain the info for UserEmail
            thing.CreateUserInfoPort();

            this.Things[thing.PlegmaThing.ThingKey] = thing;
            this._updateWebNodeThings();
        } catch (ex) {
            DebugEx.TraceException(ex, "[WebModule] Failed to update thing for module:" + this.ModuleKey, thing);
        }
    }
    // --------------------------------------------------------------------------------------------
    public GetThingsKeys(): string[] {
        return _.keys(this.Things);
    }
    // --------------------------------------------------------------------------------------------
    public SendPortEvent(thing: WebThing, port: WebThingPort, state: any, origState: any) {
        let events: Plegma.PortEvent[] = [];
        if (this.SessionBasePorts) {
            // Accumulate all state of this web module to send it at the same time
            _.forOwn(this.Things, (tvalue) => {
                let isThisThing = _.isEqual(tvalue.ThingKey, thing.ThingKey);
                let isHaveDataPorts = _.findKey(tvalue.Ports, x =>
                    (!isWebThingKey(x.PortKey)) &&
                    (x.PortType == WebThingPortType.Data) &&
                    (x.PlegmaPort.ioDirection != Plegma.ioPortDirection.Input)) != undefined;
                if (isThisThing || isHaveDataPorts)
                    _.forOwn(tvalue.Ports, (pValue) => {
                        if (pValue.PlegmaPort.ioDirection == Plegma.ioPortDirection.Output ||
                            pValue.PlegmaPort.ioDirection == Plegma.ioPortDirection.InputOutput) {
                            let isThisPort = isThisThing && _.isEqual(pValue.PortKey, port.PortKey);
                            if (isThisPort) {
                                events.push({
                                    PortKey: tvalue.ThingKey + PlegmaStatics.PlegmaAPI.KeySeparator + pValue.PortKey,
                                    State: state,
                                })
                            } else if (pValue.PortType == WebThingPortType.Data) {
                                // include the data from others things in the same page 
                                // if is webthing and is null skip it
                                if (!(isWebThingKey(pValue.PortKey) && pValue.State == null))
                                    events.push({
                                        PortKey: tvalue.ThingKey + PlegmaStatics.PlegmaAPI.KeySeparator + pValue.PortKey,
                                        State: (isThisPort) ? state : pValue.State,
                                    })
                            }
                        }
                    });
            });
        } else {
            // We need to send only the active port and the email/portal
            events.push({
                PortKey: thing.ThingKey + PlegmaStatics.PlegmaAPI.KeySeparator + port.PortKey,
                State: state,
            });
            _.forOwn(thing.Ports, p => {
                if (isWebThingKey(p.PortKey) && p.State != null) {
                    events.push({
                        PortKey: thing.ThingKey + PlegmaStatics.PlegmaAPI.KeySeparator + p.PortKey,
                        State: p.State,
                    });
                }
            });
        }
        this.WebNode?.SendPortEvents(thing, port, state, origState, events);
    }
    // --------------------------------------------------------------------------------------------
    public RenameModule(moduleName: string) {
        this.ModuleName = moduleName;
        this._updateWebNodeThings();
    }
    // --------------------------------------------------------------------------------------------
    public SendThingSet(overwrite: boolean) {
        if (this.DummyNode) return;
        DebugEx.TraceLog("Send things to cyan", overwrite);

        // Create the things list
        let plegmaThings: Plegma.Thing[] = _.map(this.Things, t => t.PlegmaThing);

        // Send the data to the server
        this.WebNode.YodiWS.SetThings(
            plegmaThings,
            overwrite ? Plegma.eThingsOperation.Overwrite : Plegma.eThingsOperation.Update,
            this.Hierarchy);
    }
    // --------------------------------------------------------------------------------------------
    public SetSessionBasePorts(sessionBasePorts: boolean) {
        this.SessionBasePorts = !this.DummyNode && sessionBasePorts;
    }
    // --------------------------------------------------------------------------------------------
}
// ================================================================================================